/**
 * useChatbot Hook
 * Custom hook for managing chatbot conversation state.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { chatbotApi } from '../services/chatbotService';
import type {
  ChatMessage,
  ConversationDetail,
  StarterQuestion as StarterQuestionAPI,
} from '../types';
import { logger } from '@utils/logger';

/**
 * Chatbot hook return type.
 */
interface UseChatbotReturn {
  /** Current conversation messages */
  messages: ChatMessage[];
  /** Current conversation ID */
  conversationId: number | null;
  /** Whether a message is being sent / AI is processing */
  isLoading: boolean;
  /** Error message if last operation failed */
  error: string | null;
  /** Starter questions from API */
  starterQuestions: StarterQuestionAPI[];
  /** Whether starter questions are loading */
  starterQuestionsLoading: boolean;
  /** Send a user message */
  sendMessage: (message: string) => Promise<void>;
  /** Start a new conversation (resets state) */
  startNewConversation: () => void;
  /** Clear error */
  clearError: () => void;
}

/**
 * Hook for chatbot conversation management.
 * Handles creating conversations, sending messages, and fetching starter questions.
 */
export const useChatbot = (): UseChatbotReturn => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [starterQuestions, setStarterQuestions] = useState<StarterQuestionAPI[]>([]);
  const [starterQuestionsLoading, setStarterQuestionsLoading] = useState(false);
  const isMounted = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Fetch starter questions on mount
  useEffect(() => {
    const fetchStarterQuestions = async () => {
      setStarterQuestionsLoading(true);
      try {
        const questions = await chatbotApi.getStarterQuestions();
        if (isMounted.current) {
          setStarterQuestions(questions);
        }
      } catch (err) {
        logger.error('Failed to fetch starter questions', err);
        // Non-critical: don't set error for starter questions
      } finally {
        if (isMounted.current) {
          setStarterQuestionsLoading(false);
        }
      }
    };

    fetchStarterQuestions();
  }, []);

  /**
   * Send a message. Creates conversation on first message.
   */
  const sendMessage = useCallback(async (message: string) => {
    if (!message.trim() || isLoading) return;

    setError(null);
    setIsLoading(true);

    // Optimistically add user message to display immediately
    const tempUserMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      senderType: 'user',
      content: message.trim(),
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUserMessage]);

    try {
      let activeConversationId = conversationId;

      // Create conversation if none exists
      if (!activeConversationId) {
        const conversation = await chatbotApi.createConversation();
        activeConversationId = conversation.id;
        if (isMounted.current) {
          setConversationId(conversation.id);
        }
      }

      // Send message and get AI response
      const assistantMessage = await chatbotApi.sendMessage(
        activeConversationId,
        message.trim()
      );

      if (isMounted.current) {
        // Replace temp user message with server-confirmed, add assistant response
        setMessages((prev) => {
          // Remove the temp message and add the assistant response
          const withoutTemp = prev.filter((m) => m.id !== tempUserMessage.id);
          // Re-add user message with proper content, and add assistant message
          const userMsg: ChatMessage = {
            id: `user-${Date.now()}`,
            senderType: 'user',
            content: message.trim(),
            createdAt: tempUserMessage.createdAt,
          };
          return [...withoutTemp, userMsg, assistantMessage];
        });
      }
    } catch (err) {
      if (isMounted.current) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to send message. Please try again.';
        setError(errorMessage);
        // Remove optimistic user message on failure
        setMessages((prev) => prev.filter((m) => m.id !== tempUserMessage.id));
      }
      logger.error('Failed to send chatbot message', err);
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, [conversationId, isLoading]);

  /**
   * Start a new conversation (resets all state).
   */
  const startNewConversation = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setError(null);
    setIsLoading(false);
  }, []);

  /**
   * Clear error state.
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    messages,
    conversationId,
    isLoading,
    error,
    starterQuestions,
    starterQuestionsLoading,
    sendMessage,
    startNewConversation,
    clearError,
  };
};
